import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Pill, Store, Truck, CheckCircle2, Clock } from 'lucide-react';
import { mockPrescriptions } from '../data/mockData';
export function PrescriptionScreen() {
  const navigate = useNavigate();
  const prescription = mockPrescriptions[0];
  const [deliveryMethod, setDeliveryMethod] = useState<'delivery' | 'pickup'>('delivery');
  const [orderStatus, setOrderStatus] = useState<'none' | 'ordered'>('none');
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="flex flex-col min-h-screen bg-slate-50" data-id="element-1163">
      <div className="bg-white px-6 pt-12 pb-4 shadow-sm sticky top-0 z-10 flex items-center justify-between" data-id="element-1164">
        <div className="flex items-center gap-3" data-id="element-1165">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-slate-50 rounded-full" data-id="element-1166">
            <ArrowLeft className="w-6 h-6 text-slate-700" data-id="element-1167" />
          </button>
          <h1 className="text-xl font-bold text-slate-900" data-id="element-1168">Prescription</h1>
        </div>
        <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-full" data-id="element-1169">
          <Download className="w-5 h-5" data-id="element-1170" />
        </button>
      </div>

      <div className="p-6 space-y-6 flex-1" data-id="element-1171">
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100" data-id="element-1172">
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100" data-id="element-1173">
            <div data-id="element-1174">
              <p className="text-sm text-slate-500" data-id="element-1175">Prescribed by</p>
              <p className="font-semibold text-slate-900" data-id="element-1176">
                {prescription.doctorName}
              </p>
            </div>
            <div className="text-right" data-id="element-1177">
              <p className="text-sm text-slate-500" data-id="element-1178">Date</p>
              <p className="font-medium text-slate-900" data-id="element-1179">{prescription.date}</p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-1180">
            {prescription.medications.map((med, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-1181">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-1182">
                  <Pill className="w-5 h-5 text-blue-600" data-id="element-1183" />
                </div>
                <div data-id="element-1184">
                  <h4 className="font-semibold text-slate-900" data-id="element-1185">
                    {med.name}{' '}
                    <span className="text-sm font-normal text-slate-500" data-id="element-1186">
                      ({med.dosage})
                    </span>
                  </h4>
                  <p className="text-sm text-slate-600 mt-1" data-id="element-1187">{med.frequency}</p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-1188">
                    Duration: {med.duration}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {orderStatus === 'none' ? <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100" data-id="element-1189">
            <h3 className="font-semibold text-slate-900 mb-4" data-id="element-1190">
              Fulfill Prescription
            </h3>

            <div className="flex gap-3 mb-6" data-id="element-1191">
              <button onClick={() => setDeliveryMethod('delivery')} className={`flex-1 p-3 rounded-xl border flex flex-col items-center gap-2 transition-colors ${deliveryMethod === 'delivery' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`} data-id="element-1192">
                <Truck className="w-6 h-6" data-id="element-1193" />
                <span className="text-sm font-medium" data-id="element-1194">Delivery</span>
              </button>
              <button onClick={() => setDeliveryMethod('pickup')} className={`flex-1 p-3 rounded-xl border flex flex-col items-center gap-2 transition-colors ${deliveryMethod === 'pickup' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`} data-id="element-1195">
                <Store className="w-6 h-6" data-id="element-1196" />
                <span className="text-sm font-medium" data-id="element-1197">Pickup</span>
              </button>
            </div>

            <button onClick={() => setOrderStatus('ordered')} className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm hover:bg-blue-700 transition-colors" data-id="element-1198">
              Send to Pharmacy
            </button>
          </div> : <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100" data-id="element-1199">
            <h3 className="font-semibold text-slate-900 mb-4" data-id="element-1200">Order Status</h3>
            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-1201">
              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-1202">
                <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-blue-600 text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" data-id="element-1203">
                  <CheckCircle2 className="w-5 h-5" data-id="element-1204" />
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-100 bg-slate-50" data-id="element-1205">
                  <div className="flex items-center justify-between space-x-2 mb-1" data-id="element-1206">
                    <div className="font-bold text-slate-900" data-id="element-1207">
                      Order Confirmed
                    </div>
                    <time className="text-xs font-medium text-slate-500" data-id="element-1208">
                      10:00 AM
                    </time>
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-1209">
                    Pharmacy has received your prescription.
                  </div>
                </div>
              </div>

              <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group" data-id="element-1210">
                <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-blue-100 text-blue-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" data-id="element-1211">
                  <Clock className="w-5 h-5" data-id="element-1212" />
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-100 bg-white" data-id="element-1213">
                  <div className="flex items-center justify-between space-x-2 mb-1" data-id="element-1214">
                    <div className="font-bold text-slate-700" data-id="element-1215">Preparing</div>
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-1216">
                    Pharmacist is packing your order.
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </motion.div>;
}